import { Component } from 'react';
import styled, { css } from 'styled-components';
import { Field } from 'formik';

import { COLORS } from '../../../helpers/constants/styleguide';
import { FlexColumn } from '../../Flex';

import { Text } from '../../typography';

/**
 * Layout, label and validation chrome shared by every input in this directory.
 *
 * @typedef {Object} InputContainerProps
 * @property {string} [id]
 * @property {React.ReactNode} [label]
 * @property {React.ReactNode} [subTitle]
 * @property {React.ReactNode} [helpMessage]
 * @property {string} [width]
 * @property {string} [height]
 * @property {boolean} [noPadding]
 * @property {boolean} [noClipping]
 * @property {'default' | 'noBorder'} [theme]
 */

/** @typedef {import('formik').FormikErrors<any>} FormikErrors */
/** @typedef {import('formik').FormikTouched<any>} FormikTouched */

/** @typedef {string | string[] | FormikErrors | FormikErrors[]} FieldError */
/** @typedef {boolean | FormikTouched | FormikTouched[]} FieldTouched */
/** @typedef {(value: unknown) => string | undefined | void} FieldValidator */
/** @typedef {(field: string, value: unknown, validate?: boolean) => unknown} SetFieldValue */
/** @typedef {(field: string, touched?: boolean, validate?: boolean) => unknown} SetFieldTouched */

/**
 * The Formik bundle every field in this directory is called with. Each
 * component reads only part of it; members a component ignores are still
 * accepted here because call sites pass the whole bundle uniformly.
 *
 * @typedef {Object} FormikFieldProps
 * @property {string} [name]
 * @property {FieldError} [error]
 * @property {FieldTouched} [touched]
 * @property {boolean} [isSubmitting]
 * @property {FieldValidator} [validate]
 * @property {SetFieldValue} [setFieldValue]
 * @property {SetFieldTouched} [setFieldTouched]
 * @property {boolean} ['data-private'] Accepted, but no component in this
 *   directory forwards it to the DOM. Call sites relying on it for redaction
 *   are not getting it.
 */

/** @typedef {InputContainerProps & FormikFieldProps} InputFieldProps */

/**
 * @typedef {Object} InputIds
 * @property {string} inputId
 * @property {string} [labelId] Undefined when the field renders no label
 * @property {string} [errorId] Undefined while no error message is shown
 */

/** @typedef {(setFocus: (focused?: boolean | null) => void, focus: boolean, ids: InputIds) => React.ReactNode} InputRenderChild */

/**
 * @typedef {Object} InputOwnProps
 * @property {InputRenderChild} children
 */

/** @typedef {InputFieldProps & InputOwnProps} InputProps */

/** @extends {Component<InputProps>} */
class Input extends Component {
  state = {
    focus: false,
  };

  handleFocusChange = (focused = null) => {
    this.setState(state => ({
      focus: focused ?? !state.focus,
    }));
  };

  render() {
    const { focus } = this.state;
    const {
      children,
      error,
      touched,
      width,
      height,
      isSubmitting,
      label,
      noPadding,
      noClipping,
      subTitle,
      helpMessage,
      theme = 'default',
      id,
    } = this.props;

    const inputId = `${id}-input`;
    const labelId = label ? `${id}-label` : undefined;
    const errorId =
      (touched || isSubmitting) && error ? `${id}-error` : undefined;

    return (
      <Wrapper id={id}>
        {label && (
          <Label id={labelId} htmlFor={inputId} focus={focus}>
            <Text bold>{label}</Text>
          </Label>
        )}
        {subTitle && <SubTitle paragraph>{subTitle}</SubTitle>}
        <InputContainer
          width={width}
          height={height}
          error={error}
          focus={focus}
          isSubmitting={isSubmitting}
          touched={touched}
          helpMessage={helpMessage}
          noClipping={noClipping}
          $theme={theme}
        >
          <Container noPadding={noPadding}>
            {children(this.handleFocusChange, focus, {
              inputId,
              labelId,
              errorId,
            })}
          </Container>
        </InputContainer>
        <ErrorMessage theme={theme}>
          {errorId ? (
            <Text
              size="small"
              color="error"
              id={errorId}
              data-testid="input-error"
            >
              {error}
            </Text>
          ) : (
            (
              <Text size="small" color="neutral">
                {helpMessage}
              </Text>
            ) || ''
          )}
        </ErrorMessage>
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  width: 100%;
`;

const Container = styled(FlexColumn)`
  flex: 1;
`;

const bottomBorder = css`
  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: inherit;
    height: 2px;
    transition: all 0.4s ease;
    ${({ focus }) => focus && `background-color:${COLORS.azure}`};
    ${({ error, touched, isSubmitting, focus }) =>
      (isSubmitting || touched) &&
      error &&
      focus &&
      `background-color:${COLORS.orange}`};
  }
`;

const overrideInputContainer = css`
  ${({ $theme, error }) =>
    $theme === 'noBorder' &&
    ` border: none;
      &:before {
        background-color: ${error ? COLORS.orange : COLORS.azure};
      }`}
  ${({ noClipping }) => noClipping && `overflow: visible;`}
`;

const InputContainer = styled(FlexColumn)`
  position: relative;
  flex: 1;
  border-radius: 3px;
  background-color: ${COLORS.white};
  border: 1px solid ${COLORS.grey};
  overflow: hidden;

  ${({ width, height }) => `
  height: ${height ?? '48px'};
  width: ${width ?? '100%'};`}

  ${bottomBorder};
  ${overrideInputContainer};
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
`;

const SubTitle = styled(Text)`
  margin-bottom: 8px;
  margin-top: 0;
  font-size: 14px;
  font-weight: 300;
`;

const overrideErrorMessage = css`
  ${({ theme }) =>
    theme === 'noBorder' &&
    `
    min-height: 0;`}
`;

const ErrorMessage = styled.div`
  margin-top: 4px;
  min-height: calc(14px * 1.5);
  ${overrideErrorMessage}
`;

const overrideFormInput = css`
  ${({ $theme, error }) =>
    $theme === 'noBorder' &&
    `
    font-size: 18px;
    font-weight: 600;
    text-align: right;
    padding-right: 0;
    color: ${error ? COLORS.orange : COLORS.azure};
    `}
`;

export const FormInput = styled(Field)`
  font-size: 16px;
  font-weight: 300;
  color: ${COLORS.darkerGrey};
  padding: 0 16px;

  ::placeholder {
    color: ${COLORS.medGrey};
    opacity: 1; /* Firefox */
  }
  ${overrideFormInput}
`;

export const FormInputStyle = styled.div`
  font-size: 20px;
  font-weight: 300;
  color: ${COLORS.darkerGrey};
  ${overrideFormInput}
`;

export default Input;
